import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useTablesContext } from "../../../../context/tablesContextProvider";
import { AuthStatusOptionModel } from "../../../../data-model/authStatusOptions";
import { QuickReviewListRequest } from "../../../../data-model/quickReviewModel";
import { TableNames } from "../../../../enums/tableNames";
import { RootState } from "../../../../redux/store/store";
import { makePostCallForNextPagesDataFetch } from "../apis/makePostCallForNextPagesDataFetch";
import { QuickReviewTableData } from "../utils/quickReviewTableUtils";
import {
  QuickReviewOrdersState,
  QUICK_REVIEW_HITS_PER_PAGE,
} from "./useQuickReviewOrdersTableData";

/**
 * Handles infinite-scroll pagination for the Quick Review board. When the
 * table's visible end index reaches the last loaded item and more pages are
 * available, automatically fetches the next page and appends the results.
 *
 * @param initialDataFetchingExecuted - Whether the initial table data load has finished
 * @param quickReviewOrdersState - Current board state (used to determine page boundaries)
 * @param setQuickReviewOrdersState - State setter for the board loading/data/error state
 * @param setTableData - State setter for the transformed table row data
 * @returns endIndex and setEndIndex to track the table's visible boundary
 */
export const useNextPageDataFetch = (
  initialDataFetchingExecuted: boolean,
  quickReviewOrdersState: QuickReviewOrdersState,
  setQuickReviewOrdersState: React.Dispatch<
    React.SetStateAction<QuickReviewOrdersState>
  >,
  setTableData: React.Dispatch<React.SetStateAction<QuickReviewTableData[]>>,
) => {
  const { data: authStatusOptions } = useSelector(
    (state: RootState) => state.authStatusOptions,
  );
  const { setTableDataForContext } = useTablesContext();
  const [endIndex, setEndIndex] = useState(0);

  // Use refs to store latest values without triggering re-renders
  const initialDataFetchingExecutedRef = useRef(initialDataFetchingExecuted);
  const setQuickReviewOrdersStateRef = useRef(setQuickReviewOrdersState);
  const setTableDataRef = useRef(setTableData);
  const setTableDataForContextRef = useRef(setTableDataForContext);

  // Keep refs updated
  useEffect(() => {
    initialDataFetchingExecutedRef.current = initialDataFetchingExecuted;
    setQuickReviewOrdersStateRef.current = setQuickReviewOrdersState;
    setTableDataRef.current = setTableData;
    setTableDataForContextRef.current = setTableDataForContext;
  }, [
    initialDataFetchingExecuted,
    setQuickReviewOrdersState,
    setTableData,
    setTableDataForContext,
  ]);

  const shouldFetchMoreData = useMemo(() => {
    return (
      endIndex === (quickReviewOrdersState.data?.items?.length ?? 0) - 1 &&
      (quickReviewOrdersState.data?.page ?? 0) <
        (quickReviewOrdersState.data?.total_pages ?? 0) - 1
    );
  }, [
    endIndex,
    quickReviewOrdersState.data?.items?.length,
    quickReviewOrdersState.data?.page,
    quickReviewOrdersState.data?.total_pages,
  ]);

  const payloadFinalWithPage = useMemo<QuickReviewListRequest>(
    () => ({
      page: (quickReviewOrdersState.data?.page ?? 0) + 1,
      hits_per_page: QUICK_REVIEW_HITS_PER_PAGE,
    }),
    [quickReviewOrdersState.data?.page],
  );

  const currentPage = quickReviewOrdersState.data?.page ?? 0;

  const fetchNextPage = useCallback(() => {
    makePostCallForNextPagesDataFetch(
      payloadFinalWithPage,
      (authStatusOptions ?? []) as AuthStatusOptionModel[],
      setQuickReviewOrdersStateRef.current,
      setTableDataRef.current,
    );
    setTableDataForContextRef.current(TableNames.QUICK_REVIEW_TABLE, {
      lastBatchFetched: currentPage + 1,
    });
  }, [payloadFinalWithPage, authStatusOptions, currentPage]);

  useEffect(() => {
    if (shouldFetchMoreData && initialDataFetchingExecutedRef.current) {
      fetchNextPage();
    }
  }, [shouldFetchMoreData, fetchNextPage]);

  return {
    endIndex,
    setEndIndex,
  };
};
